import {
  VariantQuestionModal,
  FeatureEntitlementChecker,
  EntityDrawerHookProvider,
  CloseButton,
  MockHook,
} from '@jupiterone/jupiter-web-toolkit';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles } from '@material-ui/styles';
import React from 'react';
import {
  Edit as EditIcon,
  Share as ShareIcon,
  TrendingUp as ChartIcon,
} from 'react-feather';
import { FeatureEntitlementName } from '@jupiterone/entitlement-types';
import { JupiterTheme } from '@jupiterone/jupiter-web-toolkit/src/theme/types';
import ShareModal from '@jupiterone/jupiter-web-toolkit/src/components/QueryResult/ShareModal';
import AddToInsightsMenu from '@jupiterone/jupiter-web-toolkit/src/components/QuestionResult/AddToInsightsMenu';
import { PopulateTemplateDialog } from '@jupiterone/jupiter-web-toolkit/src/components/QuestionResult/PopulateTemplateDialog';
import QuestionTrendChart from '@jupiterone/jupiter-web-toolkit/src/components/QuestionResult/QuestionTrendChart';
import TabQueryResultList from '@jupiterone/jupiter-web-toolkit/src/components/QuestionResult/TabQueryResultList/TabQueryResultList';
import useQuestionResultLogic, {
  QuestionResultComponentProps,
} from './useQuestionResultLogic';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.grey[50],
      margin: 0,
      padding: theme.spacing(0.5, 2, 0),
      height: 'inherit',
    },
    questionHeader: {
      alignItems: 'center',
      display: 'flex',
      flex: `0 0 auto`,
      margin: theme.spacing(1.5, 0),
      '& + div[data-variant="default"] > div:first-child': {
        top: -32,
      },
    },

    title: {
      marginLeft: theme.spacing(2),
      color: theme.palette.grey[500],
      fontSize: 14,
      textTransform: 'uppercase',
      lineHeight: 1.85,
      letterSpacing: 1.1,
      fontWeight: 700,
    },
    question: {
      color: theme.palette.text.primary,
      flex: '1 1 auto',
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(5),
    },
    mainContent: {
      borderTop: 'solid 1px ' + theme.palette.navy[50],
      margin: theme.spacing(0, -2),
      backgroundColor: `rgba(181, 188, 203, .25)`,
      height: 'calc(90vh - 285px)',
    },
    menuItemIcon: {
      color: theme.palette.coolGrey[500],
      marginRight: '1em',
    },
    iconButton: {
      color: theme.palette.grey[500],
      padding: theme.spacing(-1.5, -1.5, 0, 0),
      '&:hover': {
        color: theme.palette.primary.main,
      },
      '&:active': {
        color: theme.palette.primary.main,
      },
    },
  })
);

export const QuestionResultComponent = ({
  question,
  onRemoveResult,
  onUpdateResult,
  className,
  hasValidTrendData,
  setValidTrendData,
  isGlobalAdmin,
  hasFeature,
  insightsMenu,
  setInsightsMenu,
  clearInsightsMenu,
  questionModalOpen,
  questionModalToggle,
  questionModalClose,
  hasTemplates,
  templatedQueries,
  templateModalOpen,
  templateModalClose,
  setTemplatedQueries,
  shareModalOpen,
  shareModalClose,
  questionUrl,
  openShareDialog,
  selectedRowID,
  setSelectedRowID,
}: QuestionResultComponentProps) => {
  const classes = useStyles();

  return (
    <>
      <Paper className={classes.container}>
        <div className={classes.questionHeader}>
          <Typography className={classes.title}>Question</Typography>
          <Typography variant="h6" className={classes.question}>
            {question.title}
          </Typography>

          {hasValidTrendData &&
            hasFeature('addQuestionToDashboard') &&
            isGlobalAdmin && (
              <>
                <Tooltip title="Add to Insights" enterDelay={500}>
                  <IconButton
                    onClick={setInsightsMenu}
                    className={classes.iconButton}
                  >
                    <ChartIcon />
                  </IconButton>
                </Tooltip>
                <AddToInsightsMenu
                  anchorEl={insightsMenu}
                  open={Boolean(insightsMenu)}
                  onClose={clearInsightsMenu}
                  questionId={question.id}
                />
              </>
            )}

          <Tooltip title="Edit" enterDelay={500}>
            <IconButton
              onClick={questionModalToggle}
              className={classes.iconButton}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Share" enterDelay={500}>
            <IconButton
              onClick={openShareDialog}
              className={classes.iconButton}
            >
              <ShareIcon />
            </IconButton>
          </Tooltip>

          {onRemoveResult && (
            <CloseButton
              onClose={onRemoveResult}
              className={classes.iconButton}
              aria-label="Close"
            />
          )}
        </div>

        {question.showTrend && (
          <FeatureEntitlementChecker
            featureEntitlementName={
              FeatureEntitlementName.QUESTION_TREND_ANALYSIS
            }
          >
            <QuestionTrendChart
              questionId={question.id}
              onTrendDataLoaded={setValidTrendData}
              height={200}
            />
          </FeatureEntitlementChecker>
        )}

        <div className={classes.mainContent}>
          {(!hasTemplates || templatedQueries.length > 0) && (
            <EntityDrawerHookProvider
              isDocked
              propertyFindRelated
              propertyFindSimilar
              onClose={() => setSelectedRowID(null)}
              onDeleteEntity={() => setSelectedRowID(null)}
            >
              <TabQueryResultList
                className={className}
                queries={
                  templatedQueries.length ? templatedQueries : question.queries
                }
                onRemoveResult={onRemoveResult}
                parentSelectedRowID={selectedRowID}
                parentSetSelectedRowID={setSelectedRowID}
              />
            </EntityDrawerHookProvider>
          )}
        </div>
      </Paper>
      {questionModalOpen && (
        <VariantQuestionModal
          open={questionModalOpen}
          variant="edit"
          question={question}
          onClose={questionModalClose}
          onSubmit={(q) => onUpdateResult(q)}
        />
      )}
      {templateModalOpen && (
        <PopulateTemplateDialog
          open={templateModalOpen}
          question={question}
          onSubmit={setTemplatedQueries}
          onCancel={() => {
            templateModalClose();
            onRemoveResult();
          }}
        />
      )}
      {shareModalOpen && (
        <ShareModal
          open={shareModalOpen}
          onClose={shareModalClose}
          link={questionUrl}
          type="Question"
        />
      )}
    </>
  );
};

export default MockHook.withHookHoc(
  QuestionResultComponent,
  useQuestionResultLogic
);
